import { observer } from "mobx-react";
import Box from "@mui/material/Box";
import { useEffect, useState, useMemo } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";

import paths from "src/consts/paths";
import { useLocale } from "src/providers/LocaleProvider";
import { infoService } from "src/service/services";
import {
    ETournamentStatus,
    ETournamentType,
    GameModeType,
    ITournamentsNew,
    TournamentsModal,
    TournamentPair,
    IPlayer
} from "src/store/models";
import { IPrize } from "src/pages/tournaments/components/bracketContainerNew/types";
import useStores from "src/hooks/useStores";

import WinnerAnimation from "src/pages/tournaments/components/winnerAnimation";

import { useStyles } from "./styles";
import AlertTournamentModal from "./alertTournamentGame";

import startTournamentSound from "src/sounds/mp3-new/signalFly.mp3";
import newRoundSound from "src/sounds/mp3-new/round.mp3";
import TournamentSounds from "./tournamentSounds";
import { boardRoot } from "../../../core/endpoints";
import { goTo } from "../../../helpers";
import LeagueAlert from "./alertTournamentGame/LeagueAlert";
import NonWinnerModal from "../../../pages/tournaments/components/bracketContainerNew/components/brackets/league/modal/NonWinnerModal";

interface IStartTourGame {
    gameId: string | null;
    name: string;
    tournamentId: string | null;
    stage: string;
    step?: number;
    isFirstGame: boolean;
    type?: ETournamentType;
    startDate?: string;
}

interface IFinishTourGame {
    finishedTourId: string;
    time: number;
}

export interface ITournamentResult {
    firstWins: number;
    game: null
    gameId: string;
    isFinished: boolean;
    isFirstGame: boolean;
    maxNumberParticipants: number;
    myPrize: {
        title: string;
        prize: IPrize;
    };
    secondWins: number;
    step: number;
    title: string;
    tournamentId: string;
    winner: boolean;
    player1: IPlayer;
    player2: IPlayer;
    finishedTourId: string;
    type?: ETournamentType;
}

enum EPosData {
    maxNumber = 'maxNumber',
    logData = 'logData'
}

interface IGameTournamentDialog {
    showTournamentResult: ITournamentResult[]
    onNotificationClose: (status: string) => void;
    closedNotifications: any;
}


// TODO: refactor
const GameTournamentDialog = observer(({
    showTournamentResult,
    onNotificationClose,
    closedNotifications }: IGameTournamentDialog) => {
    const styles = useStyles();
    const {
        tournaments: {
            alerts: {
                gameStarted,
                gameStartedAfter,
                prepareToJackpot,
                prepareThirdOrFourth,
            },
            titles: {
                prizesTab: { rankPointShort },
            },
            bracket: {
                stagesTitles: { final, semiFinal, thrdPlaceMatch }
            }
        },
    } = useLocale();
    const {
        authStore,
        tournamentsStore,
        gamesStore,
    } = useStores();
    const history = useHistory();

    const me = authStore.currentUser;

    const isTournamentRunning = tournamentsStore.allTournamentsList?.some(
        (tournament) => tournament.status === ETournamentStatus.RUNNING
    );


    const match = useRouteMatch<{ id?: string }>({
        path: "/game/:id",
    });

    const currentGameId = match?.params?.id || undefined;

    const currentGame = gamesStore.getGameStoreById(
        currentGameId!
    )?.currentGame;

    const isRegularGame =
        currentGameId &&
        currentGame?.type &&
        currentGame?.type === GameModeType.REGULAR;

    const isTournamentGame =
        currentGameId &&
        currentGame?.type &&
        currentGame?.type === GameModeType.TOURNAMENT;

    const isGamePage = !!isRegularGame || !!isTournamentGame;
    const isMultiTablePage = useRouteMatch("/games");

    const prepareToJackpotStatus =
        tournamentsStore.tournamentInfoMessages?.prepareToJackpot;

    const lastRedirection = localStorage.getItem('last-redirection');
    const wasRedirectedAtStart = !!(lastRedirection && isTournamentRunning);


    const acceptHandler = (gameId: any) => {
        if (!gameId) return;
        authStore.toggleUserSettingsPage(false);

        goTo(boardRoot + `/game/${gameId}?userKey=${localStorage.getItem("t")}&currentUserId=${authStore.currentUser?._id}`); return;
        return;

    };

    // ACTIVE GAMES
    const showActiveTournamentGame = useMemo(() => {
        if (tournamentsStore.allTournamentsList && me) {
            // Турниры плей-офф
            const playoffResults = tournamentsStore.allTournamentsList
                .filter((tour) => {
                    return (
                        tour.status === ETournamentStatus.RUNNING &&
                        (tour.type === ETournamentType.PLAY_OFF || tour.type === ETournamentType.PLAY_OFF_LONG) &&
                        Array.isArray(tour?.pairs)
                    );
                })
                .map((mapedItem) => {
                    if (!mapedItem.pairs) return null;
                    const mappedData = mapedItem.pairs.find(
                        (pair: TournamentPair) =>
                            !pair.isFinished &&
                            !!pair.step &&
                            !!pair.gameId &&
                            !!pair.player1?._id &&
                            !!pair.player2?._id &&
                            (pair.player1?._id === me?._id ||
                                pair.player2?._id === me?._id)
                    );

                    if (!mappedData) return null;
                    if (!mapedItem) return null;


                    //@ts-ignore
                    const mappedDataLast = mapedItem.pairs.findLast(
                        (pair: TournamentPair) =>
                            !pair.isFinished &&
                            !!pair.step &&
                            !!pair.gameId &&
                            !!pair.player1?._id &&
                            !!pair.player2?._id &&
                            (pair.player1?._id === me?._id ||
                                pair.player2?._id === me?._id)
                    );

                    const calcStage = (array: TournamentPair[], obj: TournamentPair, tournament: ITournamentsNew) => {
                        if (tournament.type === ETournamentType.PLAY_OFF_LONG) {
                            return getDoubleBracketRound(array, obj);
                        } else {
                            return getSingleBracketRound(array, obj);
                        }
                    };

                    const getSingleBracketRound = (array: TournamentPair[], obj: TournamentPair) => {
                        const maxStep = Math.max(...array.map(p => p.step));
                        const playersInRound = array.filter(p => p.step === maxStep).length;

                        const titleStage = new Map([
                            [-1, thrdPlaceMatch("3rd place match")],
                            [1, final("Final")],
                            [2, semiFinal("Semi-final")]
                        ])


                        return titleStage.has(playersInRound) ? titleStage.get(playersInRound) : `1/${playersInRound}`;
                    };

                    const getDoubleBracketRound = (array: TournamentPair[], obj: TournamentPair): string => {
                        const lowerBracketPairs = array.filter(p => p.sub);

                        const maxLowerStep = Math.max(...lowerBracketPairs.map(p => p.step), 0);
                        const lowerFinal = lowerBracketPairs.find(p => p.step === maxLowerStep);

                        const uniqueLowerSteps = Array.from(new Set(lowerBracketPairs.map(p => p.step))).sort((a, b) => a - b);
                        const multipleLowerRounds = uniqueLowerSteps.length > 1;

                        // Проверяем Grand Final
                        const isGrandFinal = !obj.sub &&
                            lowerFinal?.isFinished &&
                            obj.step > lowerFinal.step;

                        if (isGrandFinal) {
                            console.log('Grand Final');
                            return 'Grand Final';
                        }

                        // Проверяем Cellar Final
                        const lastLowerMatches = lowerBracketPairs.filter(p => p.step === maxLowerStep);
                        const isCellarFinal = obj.sub &&
                            multipleLowerRounds &&
                            obj.step === maxLowerStep &&
                            lastLowerMatches.length === 1 &&
                            obj === lastLowerMatches[0];

                        if (isCellarFinal) {
                            console.log('Cellar Final');
                            return 'Cellar Final';
                        }

                        // C-Round для нижней сетки
                        if (obj.sub) {
                            const roundIndex = uniqueLowerSteps.indexOf(obj.step);
                            return `C-Round ${roundIndex + 1}`;
                        }

                        return `Round ${obj.step}`;
                    };

                    return mappedData
                        ? {
                            ...mappedData,
                            gameId: mappedData.gameId,
                            title: mapedItem.title,
                            tournamentId: mapedItem._id,
                            stage: calcStage(mapedItem.pairs, mappedDataLast, mapedItem),
                            isFirstGame: mappedData.step === 1,
                            type: mapedItem.type,
                            startDate: mappedDataLast?.game?.startedAt || mappedDataLast?.game?.createdAt || null,
                        }
                        : null;
                })
                .filter((item) => item !== null);

            // Турниры лиги
            const leagueResults = tournamentsStore.allTournamentsList
                .filter((tour) => {
                    return (
                        tour.status === ETournamentStatus.RUNNING &&
                        tour.type === ETournamentType.LEAGUE &&
                        Array.isArray(tour?.table)
                    );
                })
                .map((tour) => {
                    const playerEntry = tour.table?.find(p => p.player?._id === me._id);

                    if (!playerEntry) return null;

                    const { currentGameId, nextGameAt } = playerEntry;
                    const now = Date.now();
                    const nextGameTime = nextGameAt ? new Date(nextGameAt).getTime() : 0;

                    const currentPath = window.location.pathname;
                    const isOnCurrentGamePage = currentPath.includes(`/game/${currentGameId}`);

                    const lastExitTime = Number(localStorage.getItem("lastGameExit") || 0);
                    const cooldownAfterExit = 10000;
                    const isExitCooldownActive = now - lastExitTime < cooldownAfterExit;
                    const redirectedGames = JSON.parse(localStorage.getItem("redirectedTournamentGames") || '{}');


                    // 1: Есть активная игра
                    if (currentGameId && nextGameTime <= now) {
                        const hasBeenRedirected = redirectedGames[currentGameId];

                        if (!isOnCurrentGamePage && !hasBeenRedirected && !isExitCooldownActive) {

                            redirectedGames[currentGameId] = true;
                            localStorage.setItem("redirectedTournamentGames", JSON.stringify(redirectedGames));
                            localStorage.setItem("last-redirection", currentGameId);

                            if (currentGameId) {
                                goTo(`${boardRoot}/game/${currentGameId}?userKey=${localStorage.getItem("t")}&currentUserId=${me._id}`);
                            } else if (tour._id) {
                                goTo(`/tournaments/${tour._id}`);
                            }

                            return null;
                        }

                        if (!isOnCurrentGamePage) {
                            return {
                                gameId: currentGameId,
                                title: tour.title,
                                tournamentId: tour._id,
                                stage: "Active League Match",
                                isFirstGame: false,
                                type: ETournamentType.LEAGUE,
                                startDate: null
                            };
                        }

                        return null;
                    }

                    // 2: Игра скоро начнется
                    if (
                        nextGameAt &&
                        nextGameTime > now &&
                        playerEntry.nextOpponent?._id
                    ) {
                        return {
                            gameId: null,
                            title: tour.title,
                            tournamentId: tour._id,
                            stage: "Next League Match",
                            isFirstGame: false,
                            type: ETournamentType.LEAGUE,
                            startDate: nextGameAt
                        };
                    }

                    return null;
                })
                .filter(Boolean);


            const combinedResults = [...playoffResults, ...leagueResults];

            const currentPath = window.location.pathname;
            const isGamePage = currentPath.includes('/game/');
            const isProfilePage = currentPath.includes('/profile');
            const isCashboxPage = currentPath.includes('/cashbox');

            const gameToRedirect = combinedResults.find(game =>
                !!game?.gameId && !closedNotifications.has(game.gameId)
            );;

            const redirectedGames = JSON.parse(localStorage.getItem('redirectedTournamentGames') || '{}');
            const gameId = gameToRedirect?.gameId;

            const startDateRaw = (gameToRedirect as any)?.startDate;
            const gameStartTime = startDateRaw ? new Date(startDateRaw).getTime() : 0;
            const isStartDateValid = !isNaN(gameStartTime) && gameStartTime > 0;

            const now = Date.now();
            const msSinceGameStart = now - gameStartTime;

            const shouldRedirect =
                !!gameId &&
                isStartDateValid &&
                !isGamePage &&
                !isProfilePage &&
                !isCashboxPage &&
                msSinceGameStart >= 0 &&
                msSinceGameStart < 3000 &&
                !redirectedGames[gameId];

            if (
                shouldRedirect &&
                gameToRedirect?.type !== ETournamentType.LEAGUE
            ) {
                redirectedGames[gameId] = true;
                localStorage.setItem("redirectedTournamentGames", JSON.stringify(redirectedGames));
                localStorage.setItem("last-redirection", gameId);

                goTo(boardRoot + `/game/${gameId}?userKey=${localStorage.getItem("t")}&currentUserId=${authStore.currentUser?._id}`);
                return [];
            }

            const convertedResult: IStartTourGame[] = combinedResults.reduce<IStartTourGame[]>((accum, tourClear) => {
                if (tourClear) {
                    const isClosed =
                        (tourClear.gameId && closedNotifications.has(tourClear.gameId)) ||
                        (tourClear.tournamentId && closedNotifications.has(tourClear.tournamentId));

                    if (!isClosed) {
                        accum.push({
                            gameId: tourClear?.gameId ?? null,
                            name: tourClear.title,
                            tournamentId: tourClear.tournamentId || null,
                            stage: tourClear.stage,
                            isFirstGame: tourClear.isFirstGame,
                            type: tourClear.type,
                            startDate: tourClear.startDate,
                        });
                    }
                }
                return accum;
            }, []);

            return convertedResult
        }
    }, [tournamentsStore.allTournamentsList, closedNotifications, me]);

    const isInfoForAlert: IStartTourGame[] | [] = useMemo(() => {
        if (showActiveTournamentGame && showActiveTournamentGame.length) {
            return showActiveTournamentGame;
        }
        return [];
    }, [showActiveTournamentGame]);

    // FINISHED AND REDIRECT
    // The Alret-redirect component, if a chess player, 
    // for example, stays on the page for a long time (implemented within the technical specifications)
    const showFinishedTournamentGame: IFinishTourGame[] | [] = useMemo(() => {
        if (
            tournamentsStore.finishedAlertTournamentGame &&
            tournamentsStore.finishedAlertTournamentGame.length &&
            me && !!currentGameId
        ) {
            return []
        }
        return [];
    }, [
        tournamentsStore.finishedAlertTournamentGame,
        closedNotifications,
        me,
        isGamePage,
    ]);


    useEffect(() => {
        if (prepareToJackpotStatus && tournamentsStore.tournaments) {
            const isParticipating = tournamentsStore?.tournaments.find(
                (t) => t.type === ETournamentType.PLAY_OFF && t.participating
            );
            isParticipating &&
                infoService.sendInfo(
                    prepareToJackpot("10 min to the Jackpot!")
                );
        }
        return tournamentsStore.setErrorNull();
    }, [prepareToJackpotStatus]);


    useEffect(() => {
        if (currentGameId && !closedNotifications.has(currentGameId)) {
            onNotificationClose(currentGameId);
        }
        if (!currentGameId) {
            tournamentsStore.resetFinishedGameForAlert(null)
        }
    }, [currentGameId]);


    if (!isInfoForAlert && !showFinishedTournamentGame && !showTournamentResult) return null;

    return (
        <Box className={styles.dialogMainWrapper} component='div'>
            {tournamentsStore.allTournamentsList?.filter(tournament =>
                tournament.status === ETournamentStatus.RUNNING &&
                tournament.players?.some(player => player._id === me?._id)
            ).map(tournament => {
                const tournamentCurrentRound = tournament.pairs?.reduce((maxRound, pair) => {
                    if (pair.gameId &&
                        !pair.isFinished &&
                        (pair.player1?._id === me?._id || pair.player2?._id === me?._id) &&
                        pair.step > maxRound
                    ) {
                        return pair.step;
                    }
                    return maxRound;
                }, 0) || 0;

                const hasActiveTournamentGame = tournament.pairs?.some(
                    pair => pair.gameId &&
                        !pair.isFinished &&
                        (pair.player1?._id === me?._id || pair.player2?._id === me?._id)
                );

                return (
                    <TournamentSounds
                        key={tournament._id}
                        isTournamentRunning={tournament.status === ETournamentStatus.RUNNING}
                        currentRound={tournamentCurrentRound}
                        hasActiveGameInRound={hasActiveTournamentGame}
                        startTournamentSound={startTournamentSound}
                        newRoundSound={newRoundSound}
                        wasRedirectedAtStart={wasRedirectedAtStart}
                        isParticipant={true}
                        tournamentId={tournament._id}
                    />
                );
            })}

            {showFinishedTournamentGame.map((gameItem) => {
                return (
                    <AlertTournamentModal
                        key={gameItem.finishedTourId}
                        onClose={() => { }}
                        title={'Redirect:'}
                        tournamentGame={gameItem.finishedTourId}
                        alertAction={
                            isMultiTablePage
                                ? TournamentsModal.PUSH_TO_MATCH_GAME
                                : TournamentsModal.PUSH_TO_GAME
                        }
                        actionFunction={() => {
                            history.push(
                                `${paths.tournaments}/${gameItem.finishedTourId}`
                            );
                            tournamentsStore.resetFinishedGameForAlert(gameItem.finishedTourId)
                        }}
                        timer={gameItem.time}
                        tournamentAlertsState={
                            tournamentsStore.tournamentAlertsState
                        }
                    />
                );
            })}
            {isInfoForAlert.map((gameItem) => {
                const isLeagueType = gameItem.type === ETournamentType.LEAGUE;

                if (isLeagueType) {
                    const startDateTime = gameItem.startDate ? new Date(gameItem.startDate).getTime() : 0;
                    const secondsToGameStart = Math.max(0, Math.floor((startDateTime - Date.now()) / 1000));
                    const shouldShowLeagueAlert = secondsToGameStart > 0 && secondsToGameStart <= 10;

                    if (!shouldShowLeagueAlert) {
                        return null;
                    }

                    return (
                        <LeagueAlert
                            key={gameItem.tournamentId ?? ''}
                            onClose={() => onNotificationClose(gameItem.tournamentId ?? '')}
                            title={`Next game to ${secondsToGameStart} sec.`}
                            alertAction={TournamentsModal.CLOSE_OR_PLAY}
                            tournamentGame={gameItem.tournamentId}
                            timer={startDateTime}
                            timerSeconds={secondsToGameStart}
                            tournamentAlertsState={tournamentsStore.tournamentAlertsState}
                            tournamentId={gameItem.tournamentId ?? ''}
                            currentUserId={me?._id ?? ''}
                        />
                    );
                }

                // Стандартный алерт для других типов
                if (!gameItem.gameId) return null;

                return (
                    <AlertTournamentModal
                        key={gameItem.gameId}
                        onClose={() => {
                            if (gameItem?.gameId) {
                                onNotificationClose(gameItem.gameId);
                            } else if (isLeagueType && gameItem?.tournamentId) {
                                onNotificationClose(gameItem.tournamentId);
                            }
                        }}
                        title={
                            (() => {
                                if (gameItem.isFirstGame) {
                                    return gameStarted.compile(
                                        { title: gameItem.name ?? '' },
                                        '{{title}} started!'
                                    )
                                }
                                return `${gameItem.name}, ${gameItem.stage}`
                            })()
                        }
                        tournamentGame={gameItem.gameId}
                        alertAction={TournamentsModal.CLOSE_OR_PLAY}
                        actionFunction={() => {
                            acceptHandler(gameItem?.gameId);
                            gameItem?.gameId && onNotificationClose(gameItem?.gameId);
                        }}
                        tournamentAlertsState={
                            tournamentsStore.tournamentAlertsState
                        }
                    />
                );
            })}
            {showTournamentResult && showTournamentResult.map((result) => {
                const { myPrize: { prize, title } } = result;
                const amount = !!prize.amount && prize.amount > 0 ? `, +${prize.amount.toFixed()} Coin` : '';
                const rp = !!prize.rating && prize.rating > 0 ? `, +${prize.rating.toFixed()} ${rankPointShort("PR")}` : '';
                const alertTitle = title + amount + rp;

                // Проверяем закрытие с учетом специального идентификатора для лиги
                const notificationId = result.type === ETournamentType.LEAGUE && prize.place
                    ? `${prize.tournamentId}_place${prize.place}`
                    : prize.tournamentId;

                if (closedNotifications.has(notificationId)) {
                    return null
                }

                return (
                    <AlertTournamentModal
                        key={prize.tournamentId}
                        onClose={() => { onNotificationClose(notificationId) }}
                        title={alertTitle}
                        tournamentGame={result.finishedTourId}
                        alertAction={TournamentsModal.INFO_MESSAGE}
                        tournamentAlertsState={
                            tournamentsStore.tournamentAlertsState
                        }
                    />
                );
            })}
        </Box>
    );
});

function detectLeagueResults(tournament, currentUser) {
    if (!tournament || tournament.type !== ETournamentType.LEAGUE || tournament.status !== ETournamentStatus.FINISHED) {
        return { hasResults: false };
    }

    if (!tournament.table || tournament.table.length === 0) {
        return { hasResults: false };
    }

    const sortedTable = [...tournament.table].sort((a, b) => {
        const aChips = a.chips ?? 0;
        const bChips = b.chips ?? 0;
        const aRating = a.player?.rating ?? 0;
        const bRating = b.player?.rating ?? 0;

        if (bChips !== aChips) {
            return bChips - aChips;
        }

        return bRating - aRating;
    });

    const currentUserIndex = sortedTable.findIndex(entry => entry.player?._id === currentUser?._id);

    if (currentUserIndex === -1) {
        return { hasResults: false };
    }

    const place = currentUserIndex + 1;
    const prize = tournament.prizes.find(p => p.place === place);

    const playerEntry = tournament.table.find(entry => entry.player?._id === currentUser?._id);
    const currentGameId = playerEntry?.currentGameId || '';

    if (!prize || (prize.amount === 0 && prize.rating === 0)) {
        return {
            hasResults: false,
            isNonWinner: true,
            place,
            tournament,
            maxParticipants: sortedTable.length
        };
    }

    return {
        hasResults: true,
        place,
        prize,
        tournament,
        currentGameId
    };
}

function detectFinalMatches(tournament) {
    if (!tournament || !tournament.pairs || tournament.pairs.length === 0) {
        return { finalMatch: null, thirdPlaceMatch: null, isFinalsRound: false, isGrandFinal: false };
    }

    if (tournament.type === ETournamentType.PLAY_OFF_LONG) {
        const upperBracketPairs = tournament.pairs.filter(p => p.sub === false);
        const lowerBracketPairs = tournament.pairs.filter(p => p.sub === true);

        if (upperBracketPairs.length === 0 || lowerBracketPairs.length === 0) return { finalMatch: null, thirdPlaceMatch: null, isFinalsRound: false, isGrandFinal: false };

        const maxUpperStep = Math.max(...upperBracketPairs.map(p => p.step || 0));
        const finalRoundPairs = upperBracketPairs.filter(p => p.step === maxUpperStep);

        const finalMatch = finalRoundPairs.find(p => !p.is34Final);
        const thirdPlaceMatch = finalRoundPairs.find(p => p.is34Final === true);

        const lowerFinal = lowerBracketPairs.find(p => p.step === Math.max(...lowerBracketPairs.map(p => p.step || 0)));
        const isGrandFinal = !!finalMatch &&
            !!lowerFinal &&
            lowerFinal.isFinished &&
            finalMatch?.isFinished &&
            finalMatch.step > lowerFinal.step &&
            !finalMatch.sub;

        //console.log('detectFinalMatches', {
        //    finalMatch,
        //    lowerFinal,
        //    isGrandFinal,
        //});

        return {
            finalMatch,
            thirdPlaceMatch,
            maxStep: maxUpperStep,
            isFinalsRound: finalMatch !== undefined || thirdPlaceMatch !== undefined,
            isGrandFinal,
        };
    } else {
        const maxStep = Math.max(...tournament.pairs.map(p => p.step || 0));
        const finalStepPairs = tournament.pairs.filter(p => p.step === maxStep);

        // Проверка на финальный раунд: если среди пар на максимальном шаге есть игра за 3-е место,
        // то это финальный раунд
        const hasThirdPlaceMatch = finalStepPairs.some(p => p.is34Final === true);
        const isFinalByThirdPlace = hasThirdPlaceMatch && finalStepPairs.length > 1;

        // Альтернативная проверка через количество участников
        const firstRoundPairs = tournament.pairs.filter(p => p.step === 1);
        const totalParticipants = firstRoundPairs.length * 2;
        const expectedRounds = Math.ceil(Math.log2(totalParticipants));
        const isFinalByRoundCount = maxStep >= expectedRounds;

        // Финальный раунд, если любое из условий выполняется
        const isTrulyFinalRound = isFinalByThirdPlace || isFinalByRoundCount;

        const finalMatch = finalStepPairs.find(p => !p.is34Final);
        const thirdPlaceMatch = finalStepPairs.find(p => p.is34Final === true);

        return {
            finalMatch: isTrulyFinalRound ? finalMatch : null,
            thirdPlaceMatch: isTrulyFinalRound ? thirdPlaceMatch : null,
            maxStep,
            isFinalsRound: isTrulyFinalRound,
            isGrandFinal: false,
        };
    }
}


export const GameTournamentDialogWrapper = () => {
    const { tournamentsStore, authStore } = useStores();
    const [closedNotifications, setCloseResultAlerts] = useState<Set<string>>(() => {
        const saved = localStorage.getItem('closedNotifications');
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    const me = authStore.currentUser;
    const onNotificationClose = (key: string): void => {
        const newClosedAlerts = new Set(closedNotifications);
        newClosedAlerts.add(key);
        setCloseResultAlerts(newClosedAlerts);
        localStorage.setItem('closedNotifications', JSON.stringify(Array.from(newClosedAlerts)));
    };

    useEffect(() => {
        const leagueTournaments = tournamentsStore.allTournamentsList?.filter(
            t => t.type === ETournamentType.LEAGUE && t.status === ETournamentStatus.RUNNING
        );

        leagueTournaments?.forEach(tour => {
            const playerEntry = tour.table?.find(p => p.player?._id === me?._id);
            if (!playerEntry) return;

            const currentGameId = playerEntry.currentGameId;
            const nextGameAt = playerEntry.nextGameAt;

            const isPreparing = !currentGameId && nextGameAt;
            const wasClosed = closedNotifications.has(tour._id);

            if (isPreparing && wasClosed) {
                // Проверяем, что это НЕ призовое уведомление
                if (tour.status !== ETournamentStatus.FINISHED) {
                    const updated = new Set(closedNotifications);
                    updated.delete(tour._id);
                    setCloseResultAlerts(updated);
                    localStorage.setItem('closedNotifications', JSON.stringify(Array.from(updated)));
                }
            }
        });
    }, [tournamentsStore.allTournamentsList, me]);

    const match = useRouteMatch<{ id?: string }>({
        path: "/game/:id",
    });
    const currentGameId = match?.params?.id || undefined;


    // RESULT TOURNAMENT
    const showTournamentResult = useMemo((): ITournamentResult[] => {
        if (tournamentsStore.allTournamentsList && me) {

            const playoffResults = tournamentsStore.allTournamentsList
                .filter((tour) => {
                    if (!tour.pairs?.length || !tour.prizes?.length) return false;

                    const { finalMatch, thirdPlaceMatch, isFinalsRound, isGrandFinal } = detectFinalMatches(tour);

                    // Проверяем: 
                    // 1. Это финальный раунд? 
                    // 2. Есть ли в нем завершенная игра с участием текущего пользователя?
                    const userFinishedFinalMatch = finalMatch &&
                        finalMatch.isFinished &&
                        (finalMatch.player1?._id === me?._id || finalMatch.player2?._id === me?._id);

                    const userFinishedThirdPlaceMatch = thirdPlaceMatch &&
                        thirdPlaceMatch.isFinished &&
                        (thirdPlaceMatch.player1?._id === me?._id || thirdPlaceMatch.player2?._id === me?._id);

                    const isCorrectFinalMatch =
                        (tour.type === ETournamentType.PLAY_OFF && isFinalsRound) ||
                        (tour.type === ETournamentType.PLAY_OFF_LONG && isGrandFinal);

                    if (tour.type === ETournamentType.PLAY_OFF_LONG && !isGrandFinal) {
                        return false;
                    }

                    // Возвращаем турнир только если это финальный раунд и игра пользователя завершена
                    return isCorrectFinalMatch && (userFinishedFinalMatch || userFinishedThirdPlaceMatch);
                })
                .map((mapedItem) => {
                    if (!mapedItem.pairs) return null;
                    //@ts-ignore
                    const mappedDataLast = mapedItem.pairs.findLast(
                        (pair: TournamentPair) =>
                            !!pair.step &&
                            !!pair.gameId &&
                            !!pair.player1?._id &&
                            !!pair.player2?._id &&
                            (pair.player1?._id === me?._id || pair.player2?._id === me?._id)
                    );

                    if (!mappedDataLast) return null;

                    // Проверка завершения серии матчей
                    const matchCount = mapedItem?.gameSettings?.matchCount ?? 1;
                    const maxWins = Math.max(mappedDataLast.firstWins, mappedDataLast.secondWins);
                    const isSeriesOver = maxWins >= matchCount;


                    if (!mappedDataLast.isFinished || !isSeriesOver) {
                        return null;
                    }


                    const calcPairPosition = (obj: TournamentPair) => {
                        if (obj) {
                            const pairWinner = (obj.firstWins > obj.secondWins) ?
                                obj.player1._id : obj.player2._id;
                            return pairWinner === me?._id ? true : false
                        }
                        return false
                    }

                    const calcStage = (array: TournamentPair[], obj: TournamentPair, cond?: EPosData, prizes?: IPrize[], type?: ETournamentType) => {
                        if (type === ETournamentType.PLAY_OFF_LONG) {
                            if (cond === EPosData.maxNumber) {
                                const avangard = array.reduce((acc, pair) => acc + (pair.step === 1 ? 1 : 0), 0)
                                return avangard * 2;
                            }

                            if (cond === EPosData.logData && prizes) {
                                const maxStep = Math.max(...array
                                    .filter(p => !p.sub)
                                    .map(p => p.step || 0)
                                );

                                const isFinalMatch = obj.step === maxStep && !obj.sub;

                                if (isFinalMatch) {
                                    const isPlayer1 = obj.player1._id === me?._id;
                                    const isPlayer2 = obj.player2._id === me?._id;

                                    if (!isPlayer1 && !isPlayer2) return null;

                                    const isWinner = obj.firstWins > obj.secondWins
                                        ? isPlayer1
                                        : isPlayer2;

                                    return isWinner ? prizes[0] : prizes[1];
                                }
                            }
                            return null;
                        }

                        const avangard = array.reduce((acc, pair) => acc + (pair.step === 1 ? 1 : 0), 0);

                        if (cond === EPosData.maxNumber) {
                            return avangard * 2;
                        }

                        if (cond === EPosData.logData && prizes) {
                            const maxStep = Math.max(...array.map(p => p.step || 0));
                            const finalsInSameStep = array.filter(p => p.step === maxStep && p.isFinished);

                            if (finalsInSameStep.length >= 2) {

                                const isFirstPlaceMatch = obj === finalsInSameStep[0];

                                const isWinner = calcPairPosition(obj);

                                if (isFirstPlaceMatch) {
                                    // Это матч за 1-е место
                                    return isWinner ? prizes[0] : prizes[1];
                                } else {
                                    // Это матч за 3-е место
                                    return isWinner ? prizes[2] : (prizes.length > 3 ? prizes[3] : null);
                                }
                            } else {
                                // Стандартная логика для других матчей
                                const position = 2 ** (Math.log2(avangard * 2) - (obj.step - 1));
                                const isWinner = calcPairPosition(obj);

                                if (position && prizes) {
                                    if (isWinner && position === 2) {
                                        return prizes[0] ? prizes[0] : null;
                                    }
                                    if (!isWinner && position === 2) {
                                        return prizes[1] ? prizes[1] : null;
                                    }
                                }
                            }
                        }
                        return null;
                    }

                    const prizeResult = calcStage(
                        mapedItem.pairs,
                        mappedDataLast,
                        EPosData.logData,
                        mapedItem.prizes,
                        mapedItem.type
                    );
                    if (
                        !prizeResult ||
                        typeof prizeResult !== 'object' ||
                        (!('amount' in prizeResult) && !('rating' in prizeResult))
                    ) {
                        return null;
                    }

                    return mappedDataLast && !closedNotifications.has(mapedItem._id)
                        ? {
                            ...mappedDataLast,
                            title: mapedItem.title,
                            tournamentId: mapedItem._id,
                            isFirstGame: mappedDataLast.step === 1,
                            winner: calcPairPosition(mappedDataLast),
                            maxNumberParticipants: calcStage(
                                mapedItem.pairs,
                                mappedDataLast,
                                EPosData.maxNumber,
                                undefined,
                                mapedItem.type
                            ),
                            myPrize: {
                                title: mapedItem.title,
                                prize: calcStage(
                                    mapedItem.pairs,
                                    mappedDataLast,
                                    EPosData.logData,
                                    mapedItem.prizes,
                                    mapedItem.type
                                )
                            },
                        }
                        : null;

                })
                .filter((item) => item !== null && !!item.myPrize.prize);

            const leagueResults = tournamentsStore.allTournamentsList
                .filter(tour => tour.type === ETournamentType.LEAGUE && tour.status === ETournamentStatus.FINISHED)
                .map(tour => {
                    const leagueResult = detectLeagueResults(tour, me);

                    if (!leagueResult.hasResults) {
                        return null;
                    }

                    const placeSpecificId = `${tour._id}_place${leagueResult.place}`;

                    if (closedNotifications.has(placeSpecificId)) {
                        return null;
                    }

                    return {
                        title: tour.title,
                        tournamentId: tour._id,
                        isFirstGame: false,
                        winner: leagueResult.place === 1,
                        maxNumberParticipants: tour.table?.length || 0,
                        myPrize: {
                            title: tour.title,
                            prize: {
                                ...leagueResult.prize,
                                place: leagueResult.place,
                                tournamentId: tour._id,
                                notificationId: placeSpecificId
                            }
                        },
                        gameId: leagueResult.currentGameId || '',
                        firstWins: 0,
                        secondWins: 0,
                        isFinished: true,
                        step: 0,
                        player1: tour.table?.[0]?.player || null,
                        player2: me,
                        finishedTourId: tour._id,
                        type: ETournamentType.LEAGUE
                    };
                })
                .filter(Boolean);

            return [...playoffResults, ...leagueResults];
        }
        return [];
    }, [tournamentsStore.allTournamentsList, me, closedNotifications]);

    const showLeagueNonWinnerModals = useMemo(() => {
        if (!tournamentsStore.allTournamentsList || !me) return [];

        return tournamentsStore.allTournamentsList
            .filter(t => t.type === ETournamentType.LEAGUE && t.status === ETournamentStatus.FINISHED)
            .map(tour => {
                const result = detectLeagueResults(tour, me);

                if (result?.isNonWinner) {
                    const id = `${tour._id}_nonwinner_place${result.place}`;
                    if (!closedNotifications.has(id)) {
                        return {
                            tournamentId: tour._id,
                            title: tour.title,
                            place: result.place,
                            maxParticipants: result.maxParticipants,
                            closeKey: id
                        };
                    }
                }

                return null;
            })
            .filter(Boolean);
    }, [tournamentsStore.allTournamentsList, me, closedNotifications]);

    const showTournamentResultFull = useMemo((): ITournamentResult[] => {
        return !currentGameId
            ? showTournamentResult
            : showTournamentResult.filter(item => {
                if (item.type === ETournamentType.LEAGUE) return true;
                return item.gameId === currentGameId;
            });
    }, [currentGameId, showTournamentResult])

    if ((showTournamentResultFull && showTournamentResultFull.length) || (showLeagueNonWinnerModals && showLeagueNonWinnerModals.length)) {
        return (
            <>
                {showTournamentResultFull.map((result) => {
                    const { myPrize } = result;

                    const tournament = tournamentsStore.allTournamentsList.find(t => t._id === myPrize.prize.tournamentId);
                    if (!tournament) return null;

                    let modifiedPrize = myPrize;

                    if (tournament.type === ETournamentType.LEAGUE) {
                        const place = myPrize.prize.place || 0;
                        console.log("Лига: местo", place);

                    } else {
                        // для других типов турниров
                        const { finalMatch, thirdPlaceMatch, isFinalsRound } = detectFinalMatches(tournament);

                        // участие в финальных матчах
                        const relevantMatch = [finalMatch, thirdPlaceMatch].find(
                            p => p && p.isFinished && (p.player1?._id === me?._id || p.player2?._id === me?._id)
                        );

                        if (!relevantMatch || !relevantMatch.isFinished) return null;

                        const isPlayer1 = relevantMatch.player1._id === me?._id;
                        const isWinner = (isPlayer1 && relevantMatch.firstWins > relevantMatch.secondWins) ||
                            (!isPlayer1 && relevantMatch.secondWins > relevantMatch.firstWins);

                        let prizePlace = 0;
                        let prizesIndex = 0;

                        if (relevantMatch.is34Final) {
                            // Это матч за 3-е место
                            prizePlace = isWinner ? 3 : 4;
                            prizesIndex = isWinner ? 2 : 3;
                        } else {
                            // Это финал
                            prizePlace = isWinner ? 1 : 2;
                            prizesIndex = isWinner ? 0 : 1;
                        }

                        if (prizePlace === 0) return null;

                        const correctPrize = tournament.prizes[prizesIndex] || myPrize.prize;

                        modifiedPrize = {
                            ...myPrize,
                            prize: {
                                ...correctPrize,
                                place: prizePlace
                            }
                        };
                    }

                    if (!myPrize?.prize?.place || !myPrize?.prize?.tournamentId) {
                        return null;
                    }

                    //console.log("модалкa WinnerAnimation за место:", modifiedPrize.prize.place);


                    return (
                        <WinnerAnimation
                            key={tournament._id}
                            messageLength={15}
                            actionWithWinnerWindow={(id) => {
                                if (tournament.type === ETournamentType.LEAGUE && myPrize.prize.place) {
                                    const placeSpecificId = `${myPrize.prize.tournamentId}_place${myPrize.prize.place}`;
                                    onNotificationClose(placeSpecificId);
                                } else {
                                    onNotificationClose(myPrize.prize.tournamentId);
                                }
                            }}
                            tourPrize={{
                                ...modifiedPrize,
                            }}
                        />
                   );
                })}

                console.log('Non-winner modals:', showLeagueNonWinnerModals);


                {/* Non-winner модалки отдельно от WinnerAnimation */}
                {showLeagueNonWinnerModals
                    .filter((modal): modal is NonNullable<typeof modal> => modal !== null)
                    .map((modal) => (
                        <NonWinnerModal
                            key={modal.closeKey}
                            open={true}
                            onClose={() => {
                                onNotificationClose(modal.closeKey);
                            }}
                            place={modal.place}
                            tournamentTitle={modal.title}
                            maxParticipants={modal.maxParticipants}
                        />
                    ))}
            </>
        );
    }
    return (
        <GameTournamentDialog
            showTournamentResult={showTournamentResult}
            onNotificationClose={onNotificationClose}
            closedNotifications={closedNotifications}
        />
    )

};

export default observer(GameTournamentDialogWrapper);
